"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { ALL_CATEGORIES, ChangelogSort } from "./utils";

const EASE = [0.22, 1, 0.36, 1] as const;
const DRAW_LINE = {
  initial: { scaleX: 0 },
  animate: { scaleX: 1 },
  transition: { duration: 1.4, ease: [...EASE], delay: 0.5 },
} as const;
const REVEAL_CONTROLS = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [...EASE], delay: 0.8 },
} as const;

interface CategoryOption {
  value: string;
  label: string;
}

function ChevronDown({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      className={className}
      style={style}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true">
      <path
        d="M10 13.75L3.75 7.50002L4.625 6.62502L10 12L15.375 6.62502L16.25 7.50002L10 13.75Z"
        fill="white"
      />
    </svg>
  );
}

function CategoryDropdown({
  options,
  value,
  onChange,
}: {
  options: CategoryOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const openList = () => {
    setActive(selectedIndex);
    setOpen(true);
    requestAnimationFrame(() => listRef.current?.focus());
  };

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };

  const select = (index: number) => {
    const option = options[index];
    if (option) onChange(option.value);
    close();
  };

  const onButtonKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openList();
    }
  };

  const onListKeyDown = (event: React.KeyboardEvent) => {
    const last = options.length - 1;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => Math.min(last, i + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((i) => Math.max(0, i - 1));
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(last);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        select(active);
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
      case "Tab":
        close(false);
        break;
    }
  };

  return (
    <div
      ref={rootRef}
      className="relative w-[24.25rem]">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onButtonKeyDown}
        className="flex h-[6rem] w-full items-center justify-between rounded-[0.5rem] bg-[#121212] py-[0.5rem] pr-[0.5rem] pl-[2.25rem] text-left font-helveticaNeue text-[2.25rem] leading-[1.5] text-white/80 transition-colors hover:text-white">
        <span className="truncate">{selected?.label}</span>
        <span className="flex shrink-0 items-center rounded-[0.25rem] bg-white/[0.06] p-[1.25rem]">
          <ChevronDown
            className="h-[2.5rem] w-[2.5rem] transition-transform duration-300 ease-out"
            style={{ transform: open ? "rotate(180deg)" : undefined }}
          />
        </span>
      </button>

      <ul
        ref={listRef}
        id={listId}
        role="listbox"
        tabIndex={-1}
        aria-label="Filter by category"
        aria-activedescendant={open ? `${id}-option-${active}` : undefined}
        data-open={open}
        onKeyDown={onListKeyDown}
        className="absolute top-[6.5rem] left-0 z-30 flex w-full origin-top flex-col gap-[0.75rem] rounded-[0.5rem] bg-[#121212] p-[1rem] outline-none transition-[opacity,transform] duration-200 ease-out data-[open=false]:pointer-events-none data-[open=false]:-translate-y-[0.25rem] data-[open=false]:opacity-0">
        {options.map((option, index) => (
          <li
            key={option.value}
            id={`${id}-option-${index}`}
            role="option"
            aria-selected={option.value === value}
            onPointerEnter={() => setActive(index)}
            onClick={() => select(index)}
            className={cn(
              "cursor-pointer rounded-[0.25rem] px-[2rem] py-[1.25rem] font-helveticaNeue text-[2.25rem] leading-[1.5] whitespace-nowrap text-white/80 transition-colors",
              option.value === value && "text-white",
              open && index === active && "bg-white/[0.04] text-white",
            )}>
            {option.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SortToggle({
  value,
  onChange,
  labels,
}: {
  value: ChangelogSort;
  onChange: (value: ChangelogSort) => void;
  labels: Record<ChangelogSort, string>;
}) {
  const sorts: ChangelogSort[] = ["newest", "oldest"];

  return (
    <div
      role="radiogroup"
      aria-label="Sort releases"
      className="flex h-[6rem] rounded-[0.375rem] bg-[#121212] p-[0.5rem]">
      {sorts.map((sort) => (
        <button
          key={sort}
          type="button"
          role="radio"
          aria-checked={value === sort}
          onClick={() => onChange(sort)}
          className="flex h-full items-center rounded-[0.375rem] border border-transparent px-[3rem] font-helveticaNeue text-[2rem] leading-[1.1] whitespace-nowrap text-white/[0.56] transition-colors duration-300 hover:text-white/80 aria-checked:border-white aria-checked:bg-white aria-checked:text-black lg:px-[2rem]">
          {labels[sort]}
        </button>
      ))}
    </div>
  );
}

export default function ChangelogFilters({
  categories,
  category,
  onCategoryChange,
  sort,
  onSortChange,
  allLabel,
  sortLabels,
}: {
  categories: string[];
  category: string;
  onCategoryChange: (value: string) => void;
  sort: ChangelogSort;
  onSortChange: (value: ChangelogSort) => void;
  allLabel: string;
  sortLabels: Record<ChangelogSort, string>;
}) {
  const options: CategoryOption[] = [
    { value: ALL_CATEGORIES, label: allLabel },
    ...categories.map((label) => ({ value: label, label })),
  ];

  return (
    <div className="relative z-20 flex justify-center py-[2rem] lg:py-[1rem]">
      <motion.span
        {...DRAW_LINE}
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px origin-center bg-white/10 lg:bg-white/[0.16]"
      />
      <motion.span
        {...DRAW_LINE}
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-px origin-center bg-white/10 lg:bg-white/[0.16]"
      />
      <motion.div
        {...REVEAL_CONTROLS}
        className="flex items-start gap-[1rem]">
        <CategoryDropdown
          options={options}
          value={category}
          onChange={onCategoryChange}
        />
        <SortToggle
          value={sort}
          onChange={onSortChange}
          labels={sortLabels}
        />
      </motion.div>
    </div>
  );
}
